import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Star, FileDown, Send, Briefcase, Link2, Lock } from "lucide-react";
import { UserProfile, SavedQuestion, Application, generateId } from "@/lib/storage";
import { downloadCoverLetterPdf } from "@/lib/coverLetterPdf";
import { sanitizeAiInput, looksLikeJobDescription } from "@/lib/sanitize";
import ResumePartnerLink from "@/components/webview/ResumePartnerLink";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface Props {
  profile: UserProfile;
  savedQuestions?: SavedQuestion[];
  onSaveQuestions?: (questions: SavedQuestion[]) => void;
  onAddApplication?: (app: Application) => void;
  onSignUp?: () => void;
  /** Web portal: jump to Career Coaches tab */
  onNavigateToPartners?: () => void;
}

export default function CoverLetterTab({
  profile,
  savedQuestions = [],
  onSaveQuestions,
  onAddApplication,
  onSignUp,
  onNavigateToPartners,
}: Props) {
  const { user, subscription } = useAuth();
  const isAccessActive = subscription.subscribed;
  // AI features are available only for subscribed users.
  // Unauthenticated users must sign up (handled by UI below).
  const canUseAI = isAccessActive;
  const [jobDescription, setJobDescription] = useState("");
  const [generating, setGenerating] = useState(false);
  const [coverLetterGenerated, setCoverLetterGenerated] = useState(false);

  // Q&A state
  const [activeQTab, setActiveQTab] = useState<'custom' | 'popular'>('custom');
  const [customQuestion, setCustomQuestion] = useState("");
  const [generatingAnswer, setGeneratingAnswer] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState("");
  // savedQuestions stores only questions (answers are transient per job description)
  const [questions, setQuestions] = useState<SavedQuestion[]>(savedQuestions);
  // Tracks regenerated answers for popular questions keyed by question id
  const [popularAnswers, setPopularAnswers] = useState<Record<string, string>>({});

  // Track-job modal state
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackUrl, setTrackUrl] = useState("");
  const [trackLoading, setTrackLoading] = useState(false);

  const handleGenerateCoverLetter = async () => {
    if (!jobDescription.trim()) return;

    const { cleaned, flagged } = sanitizeAiInput(jobDescription);
    if (flagged) {
      toast.error("Your input contained content that was removed for security.");
    }
    if (!cleaned.trim()) {
      toast.error("Enter a job description.");
      return;
    }
    if (!looksLikeJobDescription(cleaned)) {
      toast.warning(
        "Paste a fuller job description (role, requirements, company) for best results."
      );
    }

    setGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-cover-letter", {
        body: { jobDescription: cleaned, profile, mode: "cover-letter" },
      });

      if (error) throw new Error(error.message || "Failed to generate");
      if (data?.error) throw new Error(data.error);

      const coverLetter = data.coverLetter || "";
      await downloadCoverLetterPdf(coverLetter);
      setCoverLetterGenerated(true);
      trackEvent("cover_letter_generated");

      // Clear custom Q&A and popular answers on new generation
      setCustomQuestion("");
      setCurrentAnswer("");
      setCurrentQuestion("");
      setPopularAnswers({});

      toast.success("Cover letter downloaded!");

      // Prompt user to track the job application
      if (onAddApplication) {
        setTrackUrl("");
        setShowTrackModal(true);
      }
    } catch (err: unknown) {
      console.error("Generation error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAnswer = async () => {
    if (!customQuestion.trim() || !jobDescription.trim()) {
      toast.error("Enter both a job description and a question");
      return;
    }

    const jd = sanitizeAiInput(jobDescription);
    const questionSan = sanitizeAiInput(customQuestion);
    if (jd.flagged || questionSan.flagged) {
      toast.error("Your input contained content that was removed for security.");
    }
    if (!jd.cleaned.trim() || !questionSan.cleaned.trim()) {
      toast.error("Enter both a job description and a question");
      return;
    }

    setGeneratingAnswer(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-cover-letter", {
        body: {
          jobDescription: jd.cleaned,
          profile,
          mode: "question",
          question: questionSan.cleaned,
        },
      });

      if (error) throw new Error(error.message || "Failed to generate");
      if (data?.error) throw new Error(data.error);

      setCurrentAnswer(data.answer || "");
      setCurrentQuestion(questionSan.cleaned);
      trackEvent("qa_answer_generated");
    } catch (err: unknown) {
      console.error("Generation error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to generate answer");
    } finally {
      setGeneratingAnswer(false);
    }
  };

  const handleStarQuestion = () => {
    if (!currentQuestion) return;
    // Save only the question text, not the answer
    const newQ: SavedQuestion = {
      id: generateId(),
      question: currentQuestion,
      answer: "", // answers are not persisted
      starred: true,
    };
    const updated = [newQ, ...questions];
    setQuestions(updated);
    onSaveQuestions?.(updated);
    toast.success("Question saved to Popular!");
  };

  const handleRemoveStarred = (id: string) => {
    const updated = questions.filter(q => q.id !== id);
    setQuestions(updated);
    onSaveQuestions?.(updated);
    setPopularAnswers(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleRegenFromPopular = async (q: SavedQuestion) => {
    if (!jobDescription.trim()) {
      toast.error("Enter a job description first");
      return;
    }

    const jd = sanitizeAiInput(jobDescription);
    const qu = sanitizeAiInput(q.question);
    if (jd.flagged || qu.flagged) {
      toast.error("Your input contained content that was removed for security.");
    }
    if (!jd.cleaned.trim()) {
      toast.error("Enter a job description first");
      return;
    }

    // Set loading state for this specific question
    setPopularAnswers(prev => ({ ...prev, [q.id]: '___loading___' }));

    try {
      const { data, error } = await supabase.functions.invoke("generate-cover-letter", {
        body: {
          jobDescription: jd.cleaned,
          profile,
          mode: "question",
          question: qu.cleaned,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      setPopularAnswers(prev => ({ ...prev, [q.id]: data.answer || "" }));
      trackEvent("qa_answer_generated");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to generate answer");
      setPopularAnswers(prev => {
        const next = { ...prev };
        delete next[q.id];
        return next;
      });
    }
  };

  const isStarred = questions.some(q => q.question === currentQuestion);

  const handleTrackJob = async () => {
    if (!onAddApplication) return;
    setTrackLoading(true);

    try {
      let company = "";
      let position = "";

      if (trackUrl.trim()) {
        const { data, error } = await supabase.functions.invoke("extract-job", {
          body: { url: trackUrl.trim() },
        });
        if (!error && !data?.error) {
          company = data.company || "";
          position = data.position || "";
        }
      }

      const app: Application = {
        id: generateId(),
        company: company || "Unknown Company",
        position: position || "Unknown Position",
        date: new Date().toISOString(),
        status: "applied",
        url: trackUrl.trim() || undefined,
      };

      onAddApplication(app);
      setShowTrackModal(false);
      toast.success(
        company && position
          ? `Tracking ${position} at ${company}`
          : "Job added to tracker — edit to add details"
      );
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to add job");
    } finally {
      setTrackLoading(false);
    }
  };

  return (
    <>
    {/* Track Job Modal */}
    <Dialog open={showTrackModal} onOpenChange={setShowTrackModal}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-primary" />
            Track this application?
          </DialogTitle>
          <DialogDescription>
            Add this job to your tracker. Paste the listing URL and we'll auto-fill the company and position for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link2 className="w-3.5 h-3.5 shrink-0" />
            Job listing URL (optional)
          </div>
          <Input
            type="url"
            placeholder="https://careers.company.com/job/..."
            value={trackUrl}
            onChange={(e) => setTrackUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !trackLoading && handleTrackJob()}
            className="text-sm"
            autoFocus
          />
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            We'll extract the company &amp; position automatically. You can always edit them in the Jobs tab.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTrackModal(false)}
            disabled={trackLoading}
          >
            Skip
          </Button>
          <Button
            size="sm"
            onClick={handleTrackJob}
            disabled={trackLoading}
            className="gap-1.5"
          >
            {trackLoading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding…</>
            ) : (
              <><Briefcase className="w-3.5 h-3.5" /> Track Job</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <div className="p-5 space-y-4 animate-fade-up">
      <div>
        <h3 className="font-display font-bold text-sm text-foreground">Cover Letter & Q&A</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Paste a job description to generate a cover letter and answers.
        </p>
      </div>

      {!subscription.loading && !isAccessActive && user && (
        <div className="rounded-xl border border-border bg-muted/50 p-4 text-center space-y-2">
          <Lock className="w-5 h-5 mx-auto text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {subscription.trialEnd ? "Your free trial has ended" : "Subscribe to unlock"}
          </p>
          <p className="text-xs text-muted-foreground">
            Subscribe to Jobily Pro to generate cover letters and Q&A answers.
          </p>
        </div>
      )}

      {!subscription.loading && !isAccessActive && !user && (
        <div className="rounded-xl border border-border bg-muted/50 p-4 text-center space-y-2">
          <Sparkles className="w-5 h-5 mx-auto text-primary" />
          <p className="text-sm font-medium text-foreground">
            Unlock AI-powered cover letters
          </p>
          <p className="text-xs text-muted-foreground">
            Sign up and upload your resume to generate tailored cover letters and Q&A answers.
          </p>
          <Button
            size="sm"
            className="w-full mt-1"
            onClick={() => {
              trackEvent("signup_clicked", { from: "cover_letter_cta" });
              onSignUp?.();
            }}
          >
            Sign up / Sign in
          </Button>
        </div>
      )}

      {/* Job Description */}
      <div>
        <Label className="text-xs text-muted-foreground">Job Description</Label>
        <Textarea
          placeholder="Paste the job description here..."
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          className="mt-1 min-h-[100px]"
          disabled={!isAccessActive}
        />
      </div>

      {/* Generate Cover Letter Button */}
      <Button
        onClick={handleGenerateCoverLetter}
        disabled={!jobDescription.trim() || generating || !isAccessActive}
        className="w-full gap-2"
      >
        {generating ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
        ) : (
          <><FileDown className="w-4 h-4" /> {coverLetterGenerated ? 'Regenerate' : 'Generate Cover Letter'}</>
        )}
      </Button>

      {coverLetterGenerated && (
        <p className="text-xs text-muted-foreground text-center">✓ Downloaded</p>
      )}

      {/* Q&A Section */}
      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex rounded-lg bg-muted p-1 gap-1">
          <button
            onClick={() => setActiveQTab('custom')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${
              activeQTab === 'custom' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
            }`}
          >
            Custom Question
          </button>
          <button
            onClick={() => setActiveQTab('popular')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${
              activeQTab === 'popular' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
            }`}
          >
            Saved ({questions.length})
          </button>
        </div>

        {activeQTab === 'custom' && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Your Question</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="e.g. Why do you want to work here?"
                  value={customQuestion}
                  onChange={(e) => setCustomQuestion(e.target.value)}
                  className="text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerateAnswer()}
                />
                <Button
                  size="sm"
                  className="shrink-0 gap-1"
                  onClick={handleGenerateAnswer}
                  disabled={!customQuestion.trim() || !jobDescription.trim() || generatingAnswer}
                >
                  {generatingAnswer ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            {currentAnswer && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">{currentQuestion}</Label>
                  <button
                    onClick={handleStarQuestion}
                    disabled={isStarred}
                    className={`flex items-center gap-1 text-xs transition-colors ${
                      isStarred ? 'text-accent cursor-default' : 'text-muted-foreground hover:text-accent'
                    }`}
                  >
                    <Star className={`w-3.5 h-3.5 ${isStarred ? 'fill-accent' : ''}`} />
                    {isStarred ? 'Saved' : 'Save'}
                  </button>
                </div>
                <Textarea
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  className="min-h-[80px] text-sm"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(currentAnswer);
                    toast.success("Copied!");
                  }}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Copy
                </button>
              </div>
            )}
          </div>
        )}

        {activeQTab === 'popular' && (
          <div className="space-y-2">
            {questions.length === 0 ? (
              <div className="text-center py-8">
                <Star className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-xs text-muted-foreground">No saved questions yet.</p>
                <p className="text-xs text-muted-foreground mt-0.5">Star a question from Custom tab to save it.</p>
              </div>
            ) : (
              questions.map(q => {
                const answer = popularAnswers[q.id];
                const isLoading = answer === '___loading___';
                return (
                  <div key={q.id} className="p-3 rounded-xl bg-card border border-border space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-medium text-foreground">{q.question}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleRegenFromPopular(q)}
                          disabled={isLoading}
                          className="text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                          title="Generate answer for current job description"
                        >
                          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleRemoveStarred(q.id)}
                          className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Star className="w-3.5 h-3.5 fill-accent text-accent" />
                        </button>
                      </div>
                    </div>
                    {answer && !isLoading ? (
                      <>
                        <Textarea
                          value={answer}
                          onChange={(e) => setPopularAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                          className="min-h-[60px] text-sm"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(answer);
                            toast.success("Copied!");
                          }}
                          className="text-xs text-primary hover:text-primary/80 transition-colors"
                        >
                          Copy
                        </button>
                      </>
                    ) : !isLoading ? (
                      <p className="text-xs text-muted-foreground italic">
                        Tap <Sparkles className="w-3 h-3 inline text-primary" /> to generate an answer for the current job.
                      </p>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {onNavigateToPartners && (
        <ResumePartnerLink onNavigate={onNavigateToPartners} />
      )}
    </div>
    </>
  );
}
